import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Exclusão definitiva de associado (at-77, item 64) — tela `/ti/expurgo`.
 *
 * 🔴 **Todas estas funções LANÇAM**, com o status HTTP em `ErroExpurgo.status`. A
 * tela precisa distinguir 403 (não é ADMIN) de 404 (CPF não existe aqui) e de 400
 * (o SGA recusou a marcação) — e, acima de tudo, **falhar em voz alta**: é uma
 * escrita destrutiva pedida pelo usuário, não um enfeite que possa sumir em
 * silêncio. Mesmo critério de `usuarioApi` (at-68) e do `fetchAcompanhamentoDeOficina`.
 */

export class ErroExpurgo extends Error {
  constructor(mensagem: string, public readonly status?: number) {
    super(mensagem);
    this.name = 'ErroExpurgo';
  }
}

function lancar(erro: any): never {
  throw new ErroExpurgo(
    extrairMensagemErro(erro, 'Não foi possível concluir a operação.'),
    erro?.response?.status,
  );
}

/** Uma linha por tabela: quanto sai de cada lugar. */
export interface InventarioExpurgo {
  veiculos: number;
  fotosDeDocumento: number;
  vistorias: number;
  fotosDeVistoria: number;
  vistoriasEnviadasAoSga: number;
  contratos: number;
  termosDoSga: number;
  termosLocais: number;
  assinaturas: number;
  produtosVinculados: number;
  leadsDeCotacao: number;
  cotacoesGeradas: number;
  rascunhosDeAdesao: number;
  divergencias: number;
  novidadesDaCarteira: number;
  atendimentosDeOficina: number;
  arquivosNoStorage: number;
}

export interface VeiculoDoExpurgo {
  id: string;
  placa: string;
  modelo: string;
  situacao: string;
  codigoVeiculo: string | null;
  excluido: boolean;
}

export interface PreviaExpurgo {
  associado: {
    id: string;
    nomeCompleto: string;
    cpfCnpj: string;
    codigoAssociado: string | null;
    volunteerCode: string;
    origem: string;
    situacaoSga: string | null;
    criadoEm: string;
  };
  veiculos: VeiculoDoExpurgo[];
  inventario: InventarioExpurgo;
  /** Já está cadastrado no SGA. */
  noSga: boolean;
  /**
   * Exige confirmar a marcação como TESTE DE SISTEMA no SGA antes de apagar.
   * ⚠️ Decidido pelo BACKEND — a tela não recalcula (convenção at-24/at-32).
   */
  exigeMarcarNoSga: boolean;
  /** Textos prontos. A tela só renderiza. */
  avisos: string[];
}

export interface ResultadoExpurgo {
  expurgado: boolean;
  inventario: InventarioExpurgo;
  marcadoNoSga: boolean;
  arquivos: { total: number; removidos: number; comFalha: string[] };
  /** Mensagem pronta para a tela. */
  mensagem: string;
}

export interface RegistroExpurgo {
  id: string;
  cpfCnpj: string;
  codigoAssociado: string | null;
  nomeCompleto: string;
  placas: string[];
  inventario: InventarioExpurgo;
  arquivosRemovidos: number;
  arquivosComFalha: string[];
  marcadoTesteNoSga: boolean;
  motivo: string;
  expurgadoPorNome: string | null;
  expurgadoEm: string;
}

export interface HistoricoExpurgos {
  itens: RegistroExpurgo[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

/** Inventário do que sairia. **Não apaga nada.** */
export async function buscarPreviaExpurgo(cpfCnpj: string): Promise<PreviaExpurgo> {
  try {
    const { data } = await http.get<PreviaExpurgo>(
      `/associado-expurgo/previa/${encodeURIComponent(cpfCnpj)}`,
    );
    return data;
  } catch (erro) {
    lancar(erro);
  }
}

/** 🔴 Irreversível. */
export async function expurgarAssociado(
  associadoId: string,
  corpo: { motivo: string; marcarTesteNoSga: boolean },
): Promise<ResultadoExpurgo> {
  try {
    const { data } = await http.delete<ResultadoExpurgo>(
      `/associado-expurgo/${associadoId}`,
      { data: corpo },
    );
    return data;
  } catch (erro) {
    lancar(erro);
  }
}

export async function listarExpurgos(
  page = 1,
  limit = 20,
): Promise<HistoricoExpurgos> {
  try {
    const { data } = await http.get<HistoricoExpurgos>('/associado-expurgo', {
      params: { page, limit },
    });
    return data;
  } catch (erro) {
    lancar(erro);
  }
}

/**
 * Tira o CPF da lista que impede a reimportação automática — o caminho para
 * quando um CPF apagado vira cliente de verdade.
 */
export async function liberarDaLista(id: string): Promise<{ liberado: boolean }> {
  try {
    const { data } = await http.delete<{ liberado: boolean }>(
      `/associado-expurgo/lista/${id}`,
    );
    return data;
  } catch (erro) {
    lancar(erro);
  }
}
